import { faArrowLeft, faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { saveActivityThunk } from '@spike/activities-action';
import {
    Activity,
    ActivityType,
    FeedingAte,
    HydrationIntake,
    ReliefBucket,
    ReliefCategory,
    RestValue
} from '@spike/activity-model';
import { Period } from '@spike/model';
import {
    ACTIVITY_CREATE_OPTIONS,
    ACTIVITY_FORM_CARD_COLORS_MAP,
    ACTIVITY_TITLE_MAP,
    HYDRATION_LABELS,
    REST_LABELS
} from 'components/Activities/Icon/ActivityIcon';
import AlertMessage from 'components/UI/V3/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import EditableCounterField from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/EditableCounterField';
import {
    getMaxDecimalsForUnit,
    getStepForUnit,
    isValidDecimalPlaces
} from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicationDosage/medicationDosageUnitsConfig';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup/RadioGroup';
import { Select } from 'components/UI/V3/Forms/Select/Select';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import { IconCard } from 'components/UI/V3/IconCard/IconCard';
import MultiSelectField, { MultiSelectFieldOption } from 'components/UI/V3/MultiSelectField';
import PhotosSelector from 'components/UI/V3/PhotosSelector';
import { PhotoWrapper } from 'components/UI/V3/PhotosSelector/PhotosSelector';
import TagsSelector from 'components/UI/V3/TagsSelector/TagsSelector';
import VideosSelector from 'components/UI/V3/VideosSelector';
import { VideoWrapper } from 'components/UI/V3/VideosSelector/VideosSelector';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import Staff from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { StaffStatus } from 'reducers/staff/StaffState';
import { dispatch, RootState } from 'store';
import { v4 as uuidv4 } from 'uuid';
import { useReportBuilder } from '../ReportBuilderProvider';
import { ReliefSection } from './ReliefSection';
import {
    emptyReliefCategory,
    reliefBucketFromCategory,
    reliefCategoryFromBuckets,
    reliefCategorySelected,
    ReliefCategoryState
} from './reliefState';
import styles from './ReportActivityForm.module.scss';

// ---- Date/time helpers ----

const getInitialBaseDate = (timeZone: string, dateLimits?: Period): Moment => {
    const now = moment().tz(timeZone);

    if (dateLimits?.from && dateLimits?.to) {
        if (now.isBefore(dateLimits.from, 'day') || now.isAfter(dateLimits.to, 'day')) {
            return dateLimits.from.clone();
        }
    }

    return now;
};

const getTimeOptionsForDate = (
    selectedDate: Moment | null,
    dateLimits?: Period
): Array<{ id: string; label: string }> => {
    const options: Array<{ id: string; label: string }> = [];

    if (!selectedDate) {
        return options;
    }

    let startHour = 0;
    let startMinute = 0;
    let endHour = 23;
    let endMinute = 59;

    if (dateLimits?.from && dateLimits?.to) {
        const selectedDateFormatted = selectedDate.format('YYYY-MM-DD');
        const fromDateFormatted = dateLimits.from.format('YYYY-MM-DD');
        const toDateFormatted = dateLimits.to.format('YYYY-MM-DD');

        if (selectedDate.isBefore(dateLimits.from, 'day') || selectedDate.isAfter(dateLimits.to, 'day')) {
            return options;
        }

        if (selectedDateFormatted === fromDateFormatted) {
            startHour = dateLimits.from.hour();
            startMinute = dateLimits.from.minute();
        }

        if (selectedDateFormatted === toDateFormatted) {
            endHour = dateLimits.to.hour();
            endMinute = dateLimits.to.minute();
        }
    }

    for (let hour = 0; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const isWithinStart = hour > startHour || (hour === startHour && minute >= startMinute);
            const isWithinEnd = hour < endHour || (hour === endHour && minute <= endMinute);

            if (isWithinStart && isWithinEnd) {
                const formattedTime = moment().hour(hour).minute(minute).second(0).format('hh:mma');
                options.push({ id: formattedTime, label: formattedTime });
            }
        }
    }

    return options;
};

const getInitialDateWithValidTime = (baseDate: Moment, options: Array<{ id: string; label: string }>): Moment => {
    if (options.length === 0) {
        return baseDate.clone();
    }

    const currentTime = baseDate.format('hh:mma');
    const currentTimeOption = options.find(option => option.id === currentTime);

    if (currentTimeOption) {
        return baseDate.clone();
    }

    const currentTimeMoment = moment(currentTime, 'hh:mma');
    let closestOption = options[0];
    let smallestDiff = Infinity;

    for (const option of options) {
        const diff = Math.abs(moment(option.id, 'hh:mma').diff(currentTimeMoment, 'minutes'));

        if (diff < smallestDiff) {
            smallestDiff = diff;
            closestOption = option;
        }
    }

    return baseDate.clone().set({
        hour: moment(closestOption.id, 'hh:mma').hour(),
        minute: moment(closestOption.id, 'hh:mma').minute()
    });
};

const toRadioOptions = <T extends string>(labels: Record<T, string>): Array<{ value: T; inlineLabel: string }> =>
    (Object.keys(labels) as Array<T>).map(value => ({ value, inlineLabel: labels[value] }));

const HYDRATION_OPTIONS = toRadioOptions(HYDRATION_LABELS);
const REST_OPTIONS = toRadioOptions(REST_LABELS);

// ---- Type-specific field state ----
//
// Exactly one activity type's fields are ever visible/editable at a time (gated by
// `selectedType`), so all type-specific fields live in one reducer instead of 13 separate
// `useState` calls. This keeps `resetForm` and edit-mode seeding to a single dispatch/init
// instead of 13 hand-written setters, and collapses what would otherwise be several unbatched
// re-renders (React 17 doesn't auto-batch `setState` calls made outside a synchronous event
// handler, e.g. after `await` in `submitHandler`) into one.

interface TypeFieldsState {
    hydrationIntake: HydrationIntake | null;
    restValue: RestValue | null;
    selectedAte: FeedingAte | null;
    medicationName: string;
    selectedMethodId: number | null;
    dosageAmount: number;
    selectedUnitId: number | null;
    peed: ReliefCategoryState;
    pooped: ReliefCategoryState;
    selectedIncidentSubtypeIds: Array<string>;
    selectedIncidentStaffIds: Array<number>;
    selectedEnrichmentSubtypeIds: Array<string>;
    selectedEnrichmentStaffIds: Array<number>;
}

type TypeFieldsAction =
    | {
          [K in keyof TypeFieldsState]: { type: 'SET_FIELD'; field: K; value: TypeFieldsState[K] };
      }[keyof TypeFieldsState]
    | { type: 'RESET' };

const emptyTypeFields = (): TypeFieldsState => ({
    hydrationIntake: null,
    restValue: null,
    selectedAte: null,
    medicationName: '',
    selectedMethodId: null,
    dosageAmount: 0.5,
    selectedUnitId: null,
    peed: emptyReliefCategory(),
    pooped: emptyReliefCategory(),
    selectedIncidentSubtypeIds: [],
    selectedIncidentStaffIds: [],
    selectedEnrichmentSubtypeIds: [],
    selectedEnrichmentStaffIds: []
});

const initTypeFields = (activityToEdit?: Activity): TypeFieldsState => {
    const base = emptyTypeFields();

    if (!activityToEdit) {
        return base;
    }

    const existingReliefBuckets =
        activityToEdit.type === ActivityType.RELIEFS
            ? (activityToEdit.activitySubtype as unknown as Array<ReliefBucket> | undefined)
            : undefined;

    return {
        ...base,
        hydrationIntake:
            activityToEdit.type === ActivityType.HYDRATION
                ? (activityToEdit.activitySubtype?.[0] as HydrationIntake) ?? null
                : base.hydrationIntake,
        restValue:
            activityToEdit.type === ActivityType.REST
                ? (activityToEdit.activitySubtype?.[0] as RestValue) ?? null
                : base.restValue,
        selectedAte: activityToEdit.type === ActivityType.FEEDING ? activityToEdit.ate ?? null : base.selectedAte,
        medicationName:
            activityToEdit.type === ActivityType.MEDICATION ? activityToEdit.medicationName ?? '' : base.medicationName,
        selectedMethodId:
            activityToEdit.type === ActivityType.MEDICATION
                ? activityToEdit.medicationMethodId ?? null
                : base.selectedMethodId,
        dosageAmount:
            activityToEdit.type === ActivityType.MEDICATION
                ? activityToEdit.dosageAmount != null
                    ? Number(activityToEdit.dosageAmount)
                    : 0.5
                : base.dosageAmount,
        selectedUnitId:
            activityToEdit.type === ActivityType.MEDICATION ? activityToEdit.dosageUnitId ?? null : base.selectedUnitId,
        peed: reliefCategoryFromBuckets(existingReliefBuckets, ReliefCategory.PEED),
        pooped: reliefCategoryFromBuckets(existingReliefBuckets, ReliefCategory.POOPED),
        selectedIncidentSubtypeIds:
            activityToEdit.type === ActivityType.INCIDENT
                ? (activityToEdit.activitySubtype as Array<string>) ?? []
                : base.selectedIncidentSubtypeIds,
        selectedIncidentStaffIds:
            activityToEdit.type === ActivityType.INCIDENT
                ? activityToEdit.involvedStaffIds ?? []
                : base.selectedIncidentStaffIds,
        selectedEnrichmentSubtypeIds:
            activityToEdit.type === ActivityType.ENRICHMENT
                ? (activityToEdit.activitySubtype as Array<string>) ?? []
                : base.selectedEnrichmentSubtypeIds,
        selectedEnrichmentStaffIds:
            activityToEdit.type === ActivityType.ENRICHMENT
                ? activityToEdit.involvedStaffIds ?? []
                : base.selectedEnrichmentStaffIds
    };
};

function typeFieldsReducer(state: TypeFieldsState, action: TypeFieldsAction): TypeFieldsState {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'RESET':
            return emptyTypeFields();
        default:
            return state;
    }
}

// ---- ReportActivityForm ----

interface ReportActivityFormProps {
    activityToEdit?: Activity;
    petId: number;
    bookingId: number;
    dateLimits?: Period;
    onDone?: () => void;
}

const ReportActivityFormComponent: React.FC<ReportActivityFormProps> = ({
    activityToEdit,
    petId,
    bookingId,
    dateLimits,
    onDone
}) => {
    const timeZone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();
    const { addActivity, updateActivity } = useReportBuilder();

    const isLoading = useSelector((state: RootState) => state.activities.loading);
    const marketplaceId = useSelector((state: RootState) => state.marketplace.marketplace.id);
    const masterDataActivitySubtypes = useSelector((state: RootState) => state.masterData.data.activitySubtypes);
    const feedingAteValues = useSelector((state: RootState) => state.masterData.data.feedingAteValues);
    const medicationMethodTypes = useSelector((state: RootState) => state.masterData.data.medicationMethodTypes);
    const medicationDosageUnits = useSelector((state: RootState) => state.masterData.data.medicationDosageUnits);
    const staffMembers = useSelector((state: RootState) => state.staff.staff);
    const staffStatus = useSelector((state: RootState) => state.staff.status);

    const isEditMode = Boolean(activityToEdit);

    const initialBaseDate = getInitialBaseDate(timeZone, dateLimits);
    const fallbackMinDate = dateLimits?.from ?? initialBaseDate.clone().subtract(1, 'year');
    const fallbackMaxDate = dateLimits?.to ?? initialBaseDate;
    const initialTimeOptions = getTimeOptionsForDate(initialBaseDate, dateLimits);
    const initialDate = getInitialDateWithValidTime(activityToEdit?.date ?? initialBaseDate, initialTimeOptions);

    const [isDateSelectorExpanded, setIsDateSelectorExpanded] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedType, setSelectedType] = React.useState<ActivityType | null>(activityToEdit?.type ?? null);
    const [selectedDate, setSelectedDate] = React.useState<Moment | null>(initialDate);
    const [note, setNote] = React.useState(activityToEdit?.note ?? '');

    const [typeFields, dispatchTypeFields] = React.useReducer(typeFieldsReducer, activityToEdit, initTypeFields);

    const setTypeField = React.useCallback(
        <K extends keyof TypeFieldsState>(field: K, value: TypeFieldsState[K]) =>
            dispatchTypeFields({ type: 'SET_FIELD', field, value }),
        []
    );

    const {
        hydrationIntake,
        restValue,
        selectedAte,
        medicationName,
        selectedMethodId,
        dosageAmount,
        selectedUnitId,
        peed,
        pooped,
        selectedIncidentSubtypeIds,
        selectedIncidentStaffIds,
        selectedEnrichmentSubtypeIds,
        selectedEnrichmentStaffIds
    } = typeFields;

    const [photos, setPhotos] = React.useState<Array<PhotoWrapper>>(
        activityToEdit?.photos.map(photo => ({
            id: photo.id,
            url: photo.url,
            fileToUpload: photo.file,
            destroy: photo.destroy
        })) ?? []
    );
    const [videos, setVideos] = React.useState<Array<VideoWrapper>>(
        activityToEdit?.videos.map(video => ({
            id: video.id,
            url: video.url,
            fileToUpload: video.file,
            destroy: video.destroy
        })) ?? []
    );
    const [savedActivity, setSavedActivity] = React.useState<Activity | null>(null);

    const timeOptions = React.useMemo(
        () => getTimeOptionsForDate(selectedDate, dateLimits),
        [selectedDate, dateLimits]
    );

    const incidentSubtypeOptions = React.useMemo<Array<MultiSelectFieldOption<string>>>(
        () =>
            (masterDataActivitySubtypes?.incident ?? []).map(option => ({
                id: option.id,
                label: option.name
            })),
        [masterDataActivitySubtypes]
    );

    const enrichmentSubtypeOptions = React.useMemo<Array<MultiSelectFieldOption<string>>>(
        () =>
            (masterDataActivitySubtypes?.enrichment ?? []).map(option => ({
                id: option.id,
                label: option.name
            })),
        [masterDataActivitySubtypes]
    );

    const medicationMethodOptions = React.useMemo(
        () => (medicationMethodTypes ?? []).map(m => ({ label: m.name, value: String(m.id) })),
        [medicationMethodTypes]
    );

    const medicationUnitOptions = React.useMemo(
        () => (medicationDosageUnits ?? []).map(u => ({ label: u.name, value: String(u.id) })),
        [medicationDosageUnits]
    );

    const selectedUnitName = React.useMemo(
        () => (medicationDosageUnits ?? []).find(u => u.id === selectedUnitId)?.name ?? '',
        [medicationDosageUnits, selectedUnitId]
    );

    const dosageStep = React.useMemo(() => getStepForUnit(selectedUnitName), [selectedUnitName]);

    const dosageMaxDecimals = React.useMemo(
        () => (selectedUnitName ? getMaxDecimalsForUnit(selectedUnitName) : undefined),
        [selectedUnitName]
    );

    const dosageError = React.useMemo<string | null>(() => {
        if (dosageAmount == null || !selectedUnitName || dosageMaxDecimals === undefined) {
            return null;
        }

        return isValidDecimalPlaces(dosageAmount, dosageMaxDecimals)
            ? null
            : `Maximum ${dosageMaxDecimals} decimal place${
                  dosageMaxDecimals === 1 ? '' : 's'
              } allowed for ${selectedUnitName}.`;
    }, [dosageAmount, selectedUnitName, dosageMaxDecimals]);

    const activeStaff = React.useMemo<Array<Staff>>(
        () => staffMembers.filter(member => member.active && !member.deleted),
        [staffMembers]
    );

    const staffTagOptions = React.useMemo(
        () =>
            activeStaff.map(member => ({
                id: member.id ?? 0,
                name: `${member.person.firstName} ${member.person.lastName}`,
                image: member.person.avatar
            })),
        [activeStaff]
    );

    const selectedIncidentSubtypeOptions = React.useMemo(
        () => incidentSubtypeOptions.filter(option => selectedIncidentSubtypeIds.includes(option.id)),
        [incidentSubtypeOptions, selectedIncidentSubtypeIds]
    );

    const selectedIncidentStaffOptions = React.useMemo(
        () => staffTagOptions.filter(staff => selectedIncidentStaffIds.includes(staff.id)),
        [staffTagOptions, selectedIncidentStaffIds]
    );

    const selectedEnrichmentSubtypeOptions = React.useMemo(
        () => enrichmentSubtypeOptions.filter(option => selectedEnrichmentSubtypeIds.includes(option.id)),
        [enrichmentSubtypeOptions, selectedEnrichmentSubtypeIds]
    );

    const selectedEnrichmentStaffOptions = React.useMemo(
        () => staffTagOptions.filter(staff => selectedEnrichmentStaffIds.includes(staff.id)),
        [staffTagOptions, selectedEnrichmentStaffIds]
    );

    const isStaffSelectorLoading = React.useMemo(
        () => staffStatus === StaffStatus.Fetching && staffMembers.length === 0,
        [staffStatus, staffMembers.length]
    );

    const isValid = React.useMemo(() => {
        if (!selectedType) return false;

        if (selectedType === ActivityType.PHOTO || selectedType === ActivityType.INCIDENT) {
            const validPhotos = photos.filter(photo => !photo.destroy);

            if (validPhotos.length > 10) return false;
            if (selectedType === ActivityType.PHOTO) return validPhotos.length > 0;
        }

        if (selectedType === ActivityType.VIDEO) {
            const validVideos = videos.filter(video => !video.destroy);
            return validVideos.length > 0 && validVideos.length <= 3;
        }

        if (selectedType === ActivityType.NOTE) return note.trim().length > 0;

        if (selectedType === ActivityType.INCIDENT) return selectedIncidentSubtypeIds.length > 0;

        if (selectedType === ActivityType.ENRICHMENT) return selectedEnrichmentSubtypeIds.length > 0;

        if (selectedType === ActivityType.FEEDING) return Boolean(selectedAte);

        if (selectedType === ActivityType.MEDICATION) {
            return Boolean(medicationName.trim() && selectedMethodId && selectedUnitId && !dosageError);
        }

        if (selectedType === ActivityType.HYDRATION) return Boolean(hydrationIntake);

        if (selectedType === ActivityType.REST) return Boolean(restValue);

        if (selectedType === ActivityType.RELIEFS) {
            return (peed.enabled && reliefCategorySelected(peed)) || (pooped.enabled && reliefCategorySelected(pooped));
        }

        return true;
    }, [selectedType, photos, videos, note, typeFields, dosageError]);

    const timeChangeHandler = React.useCallback(
        (value: string) => {
            const date = selectedDate ?? initialDate;
            const time = moment(value, 'hh:mma');

            setSelectedDate(
                date.clone().set({
                    hour: time.hour(),
                    minute: time.minute()
                })
            );
        },
        [selectedDate, initialDate]
    );

    const dateChangeHandler = React.useCallback(
        (value: Moment | null) => {
            if (!value) {
                return setSelectedDate(null);
            }

            const newTimeOptions = getTimeOptionsForDate(value, dateLimits);
            const currentTime = (selectedDate ?? initialDate).format('hh:mma');
            const currentTimeValid = newTimeOptions.find(option => option.id === currentTime);

            if (newTimeOptions.length === 0) {
                setSelectedDate(value.clone());
                setIsDateSelectorExpanded(false);
                return;
            }

            const timeToUse = currentTimeValid ? currentTime : newTimeOptions[0].id;
            const timeToUseMoment = moment(timeToUse, 'hh:mma');

            setSelectedDate(
                value.clone().set({
                    hour: timeToUseMoment.hour(),
                    minute: timeToUseMoment.minute()
                })
            );
            setIsDateSelectorExpanded(false);
        },
        [selectedDate, initialDate, dateLimits]
    );

    const resetForm = React.useCallback(() => {
        setSelectedType(null);
        setNote('');
        dispatchTypeFields({ type: 'RESET' });
        setPhotos([]);
        setVideos([]);
        setSavedActivity(null);
        setError(null);
    }, []);

    const submitHandler = React.useCallback(async () => {
        if (!selectedType || !selectedDate) return;

        let activitySubtype: Array<string | ReliefBucket> = [];

        if (selectedType === ActivityType.INCIDENT) {
            activitySubtype = selectedIncidentSubtypeIds;
        } else if (selectedType === ActivityType.ENRICHMENT) {
            activitySubtype = selectedEnrichmentSubtypeIds;
        } else if (selectedType === ActivityType.HYDRATION && hydrationIntake) {
            activitySubtype = [hydrationIntake];
        } else if (selectedType === ActivityType.REST && restValue) {
            activitySubtype = [restValue];
        } else if (selectedType === ActivityType.RELIEFS) {
            activitySubtype = [
                ...(peed.enabled ? [reliefBucketFromCategory(ReliefCategory.PEED, peed)] : []),
                ...(pooped.enabled ? [reliefBucketFromCategory(ReliefCategory.POOPED, pooped)] : [])
            ];
        }

        const selectedInvolvedStaffIds =
            selectedType === ActivityType.INCIDENT
                ? selectedIncidentStaffIds
                : selectedType === ActivityType.ENRICHMENT
                ? selectedEnrichmentStaffIds
                : [];

        const savedActivityResult = await dispatch(
            saveActivityThunk(apiClientWrapper, {
                uuid: uuidv4(),
                bookingId,
                marketplaceId: Number(marketplaceId),
                type: selectedType,
                date: selectedDate,
                pets: [],
                petIds: [petId],
                activitySubtype,
                involvedStaffIds: selectedInvolvedStaffIds,
                note,
                sharedAt: null,
                ate: selectedType === ActivityType.FEEDING ? selectedAte ?? undefined : undefined,
                medicationName: selectedType === ActivityType.MEDICATION ? medicationName : undefined,
                medicationMethodId:
                    selectedType === ActivityType.MEDICATION ? selectedMethodId ?? undefined : undefined,
                dosageAmount: selectedType === ActivityType.MEDICATION ? dosageAmount : undefined,
                dosageUnitId: selectedType === ActivityType.MEDICATION ? selectedUnitId ?? undefined : undefined,
                photos: photos.map(photo => ({
                    id: photo.id,
                    url: photo.url,
                    file: photo.fileToUpload as File,
                    destroy: photo.destroy
                })),
                videos: videos.map(video => ({
                    id: video.id,
                    url: video.url,
                    file: video.fileToUpload as File,
                    destroy: video.destroy
                })),

                ...(activityToEdit && {
                    id: activityToEdit.id,
                    uuid: activityToEdit.uuid
                })
            })
        ).catch(() => {
            setError('Failed to save activity, please try again.');
        });

        if (!savedActivityResult) {
            return;
        }

        if (isEditMode) {
            updateActivity(savedActivityResult);
            onDone?.();
        } else {
            addActivity(savedActivityResult);
            setSavedActivity(savedActivityResult);
        }
    }, [
        selectedType,
        note,
        apiClientWrapper,
        bookingId,
        marketplaceId,
        typeFields,
        photos,
        videos,
        selectedDate,
        petId,
        isEditMode,
        activityToEdit,
        addActivity,
        updateActivity,
        onDone
    ]);

    React.useEffect(() => {
        const requiresStaff = selectedType === ActivityType.INCIDENT || selectedType === ActivityType.ENRICHMENT;

        if (!requiresStaff || staffMembers.length > 0 || staffStatus === StaffStatus.Fetching) {
            return;
        }

        dispatch(fetchStaffThunk());
    }, [selectedType, staffMembers.length, staffStatus]);

    React.useEffect(() => {
        setError(null);
    }, [selectedType, selectedDate, note, photos, videos, typeFields]);

    const title = isEditMode
        ? `Edit ${ACTIVITY_TITLE_MAP[activityToEdit!.type] ?? 'Activity'}`
        : selectedType
        ? `Add ${ACTIVITY_TITLE_MAP[selectedType] ?? 'Activity'}`
        : 'Add Activity';

    if (savedActivity && !isEditMode) {
        return (
            <div className={styles.successState}>
                <div className={styles.successIcon}>
                    <FontAwesomeIcon icon={faCircleCheck} />
                </div>
                <p className={styles.successTitle}>Activity Added!</p>
                <p className={styles.successMessage}>The activity has been added to the timeline.</p>
                <Button
                    label="Add Another Activity"
                    variant="green"
                    size="large"
                    onClick={resetForm}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {!selectedType && !isEditMode && (
                <div className={styles.typeGrid}>
                    {ACTIVITY_CREATE_OPTIONS.map(option => (
                        <IconCard
                            key={option.id}
                            title={option.label}
                            icon={option.icon}
                            backgroundColor={ACTIVITY_FORM_CARD_COLORS_MAP[option.id]}
                            onClick={() => setSelectedType(option.id)}
                        />
                    ))}
                </div>
            )}

            {selectedType && (
                <div className={styles.formHeader}>
                    <button
                        type="button"
                        className={styles.formHeaderBack}
                        onClick={isEditMode ? onDone : resetForm}
                        aria-label="Back"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h2 className={styles.formHeaderTitle}>{title}</h2>
                </div>
            )}

            {selectedType && (
                <div className={styles.fields}>
                    {error && (
                        <AlertMessage
                            icon={faCircleExclamation}
                            bdColor="#EF4F57"
                            bgColor="#FBB9BC33"
                            iconColor="#EF4F57"
                        >
                            {error}
                        </AlertMessage>
                    )}

                    <div className={styles.formGroupGrid}>
                        <ExpandableSelector
                            title="Date"
                            id="date-selector"
                            expanded={isDateSelectorExpanded}
                            childrenClassName={styles.dateSelector}
                            label={selectedDate?.format('MMM, DD YYYY')}
                            onExpand={() => setIsDateSelectorExpanded(!isDateSelectorExpanded)}
                            onClickAway={() => setIsDateSelectorExpanded(false)}
                        >
                            <DatePicker
                                minDate={dateLimits?.from ?? fallbackMinDate}
                                maxDate={dateLimits?.to ?? fallbackMaxDate}
                                timeZone={timeZone}
                                value={selectedDate}
                                onChange={dateChangeHandler}
                            />
                        </ExpandableSelector>

                        {selectedType !== ActivityType.RELIEFS && (
                            <TimeSelector
                                label="Time"
                                value={timeOptions.find(option => option.id === selectedDate?.format('hh:mma'))}
                                options={timeOptions}
                                onChange={timeChangeHandler}
                            />
                        )}
                    </div>

                    {selectedType === ActivityType.INCIDENT && (
                        <>
                            <MultiSelectField
                                label="Incident Type"
                                placeholder="Select Incident"
                                options={incidentSubtypeOptions}
                                value={selectedIncidentSubtypeOptions}
                                onChange={options =>
                                    setTypeField(
                                        'selectedIncidentSubtypeIds',
                                        options.map(option => option.id)
                                    )
                                }
                            />

                            <TagsSelector
                                title="Staff Involved"
                                direction="down"
                                addButtonColor="black"
                                selectAllLabel="All Members"
                                selectAllIcon={faUsers}
                                tags={staffTagOptions}
                                selected={selectedIncidentStaffOptions}
                                loading={isStaffSelectorLoading}
                                onSelect={staffId =>
                                    setTypeField(
                                        'selectedIncidentStaffIds',
                                        selectedIncidentStaffIds.includes(staffId)
                                            ? selectedIncidentStaffIds
                                            : [...selectedIncidentStaffIds, staffId]
                                    )
                                }
                                onSelectAll={ids => setTypeField('selectedIncidentStaffIds', Array.from(new Set(ids)))}
                                onRemove={staffId =>
                                    setTypeField(
                                        'selectedIncidentStaffIds',
                                        selectedIncidentStaffIds.filter(id => id !== staffId)
                                    )
                                }
                            />
                        </>
                    )}

                    {selectedType === ActivityType.ENRICHMENT && (
                        <>
                            <MultiSelectField
                                label="Activity Type"
                                placeholder="Select Activity"
                                options={enrichmentSubtypeOptions}
                                value={selectedEnrichmentSubtypeOptions}
                                onChange={options =>
                                    setTypeField(
                                        'selectedEnrichmentSubtypeIds',
                                        options.map(option => option.id)
                                    )
                                }
                            />

                            <TagsSelector
                                title="Staff"
                                direction="down"
                                addButtonColor="black"
                                selectAllLabel="All Members"
                                selectAllIcon={faUsers}
                                tags={staffTagOptions}
                                selected={selectedEnrichmentStaffOptions}
                                loading={isStaffSelectorLoading}
                                onSelect={staffId =>
                                    setTypeField(
                                        'selectedEnrichmentStaffIds',
                                        selectedEnrichmentStaffIds.includes(staffId)
                                            ? selectedEnrichmentStaffIds
                                            : [...selectedEnrichmentStaffIds, staffId]
                                    )
                                }
                                onSelectAll={ids =>
                                    setTypeField('selectedEnrichmentStaffIds', Array.from(new Set(ids)))
                                }
                                onRemove={staffId =>
                                    setTypeField(
                                        'selectedEnrichmentStaffIds',
                                        selectedEnrichmentStaffIds.filter(id => id !== staffId)
                                    )
                                }
                            />
                        </>
                    )}

                    {selectedType === ActivityType.FEEDING && (
                        <RadioGroup
                            label="Ate"
                            value={selectedAte as FeedingAte}
                            options={(feedingAteValues ?? []).map(v => ({
                                value: v.id as FeedingAte,
                                inlineLabel: v.name
                            }))}
                            onChange={v => setTypeField('selectedAte', v)}
                        />
                    )}

                    {selectedType === ActivityType.MEDICATION && (
                        <>
                            <Input
                                label="Medication Name"
                                placeholder="Medication Name"
                                value={medicationName}
                                autoFocus={false}
                                onChange={e => setTypeField('medicationName', e.target.value)}
                            />
                            <div className={styles.methodDosageRow}>
                                <Select
                                    label="Method"
                                    placeholder="Select Method"
                                    options={medicationMethodOptions}
                                    value={selectedMethodId ? String(selectedMethodId) : undefined}
                                    onChange={val => setTypeField('selectedMethodId', Number(val))}
                                />
                                <div className={styles.dosageField}>
                                    <Label>Dosage</Label>
                                    <div className={styles.dosagePill}>
                                        <EditableCounterField
                                            className={styles.dosageCounter}
                                            value={dosageAmount}
                                            minLimit={0}
                                            step={dosageStep}
                                            onChange={v => setTypeField('dosageAmount', v)}
                                        />
                                        <div className={styles.dosageDivider} />
                                        <Dropdown>
                                            <DropdownTrigger>
                                                <div className={styles.unitTrigger}>
                                                    <span>
                                                        {medicationUnitOptions.find(
                                                            o => o.value === String(selectedUnitId)
                                                        )?.label ?? 'Unit'}
                                                    </span>
                                                    <FontAwesomeIcon icon={faChevronDown} />
                                                </div>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                className={styles.unitDropdown}
                                                align="end"
                                            >
                                                {medicationUnitOptions.map(opt => (
                                                    <DropdownItem
                                                        key={opt.value}
                                                        checked={opt.value === String(selectedUnitId)}
                                                        onSelect={() =>
                                                            setTypeField('selectedUnitId', Number(opt.value))
                                                        }
                                                    >
                                                        {opt.label}
                                                    </DropdownItem>
                                                ))}
                                            </DropdownContent>
                                        </Dropdown>
                                    </div>
                                    {dosageError && (
                                        <p
                                            className={styles.dosageError}
                                            role="alert"
                                        >
                                            {dosageError}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {selectedType === ActivityType.HYDRATION && (
                        <RadioGroup<HydrationIntake | null>
                            label="Water Intake"
                            className={styles.radioRow}
                            value={hydrationIntake}
                            options={HYDRATION_OPTIONS}
                            onChange={v => setTypeField('hydrationIntake', v)}
                        />
                    )}

                    {selectedType === ActivityType.REST && (
                        <RadioGroup<RestValue | null>
                            label="Have they rested?"
                            className={styles.radioRow}
                            value={restValue}
                            options={REST_OPTIONS}
                            onChange={v => setTypeField('restValue', v)}
                        />
                    )}

                    {selectedType === ActivityType.RELIEFS && (
                        <>
                            <ReliefSection
                                title="Peed"
                                noteLabel="Pee Notes"
                                value={peed}
                                onChange={v => setTypeField('peed', v)}
                            />
                            <ReliefSection
                                title="Pooped"
                                noteLabel="Poo Notes"
                                value={pooped}
                                onChange={v => setTypeField('pooped', v)}
                            />
                        </>
                    )}

                    {[ActivityType.PHOTO, ActivityType.INCIDENT, ActivityType.ENRICHMENT].includes(
                        selectedType as ActivityType
                    ) && (
                        <PhotosSelector
                            size="small"
                            title="Photos"
                            photos={photos}
                            multipleSelection
                            onChange={setPhotos}
                        />
                    )}

                    {selectedType === ActivityType.VIDEO && (
                        <VideosSelector
                            size="small"
                            title="Videos"
                            videos={videos}
                            multipleSelection
                            onChange={setVideos}
                        />
                    )}

                    {selectedType !== ActivityType.RELIEFS && (
                        <Textarea
                            value={note}
                            maxLength={500}
                            label={
                                selectedType === ActivityType.INCIDENT
                                    ? 'Incident Description'
                                    : selectedType === ActivityType.FEEDING
                                    ? 'Feeding Notes'
                                    : selectedType === ActivityType.MEDICATION
                                    ? 'Medication Notes'
                                    : selectedType === ActivityType.HYDRATION
                                    ? 'Hydration Notes'
                                    : selectedType === ActivityType.REST
                                    ? 'Rest Notes'
                                    : 'Activity Notes'
                            }
                            placeholder="Notes here..."
                            onChange={e => setNote(e.target.value)}
                        />
                    )}

                    <div className={styles.footer}>
                        <Button
                            label={isEditMode ? 'Save Changes' : 'Add Activity'}
                            variant="green"
                            size="large"
                            disabled={!isValid}
                            isLoading={isLoading}
                            onClick={submitHandler}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export const ReportActivityForm = React.memo(ReportActivityFormComponent);
